/**
 * The print guide is a trade secret (owner, 2026-10-13): only a super-admin sees its colour.
 * Every other signed-in role sees the same image with the guide keyed out over white.
 */
import { useEffect, useState } from "react";
import { useSessionStore } from "../stores/session.js";
import { keyImage } from "../transparent/keying.js";

/** `rgba` with the guide keyed out and laid over white, in place. An image with no guide is unchanged. */
export function guideOverWhite(rgba: Uint8ClampedArray, width: number, height: number): void {
  const keyed = keyImage(rgba, width, height)?.out;
  if (!keyed) return;
  for (let i = 0; i < keyed.length; i += 4) {
    const a = keyed[i + 3] / 255;
    for (let c = 0; c < 3; c++) rgba[i + c] = Math.round(keyed[i + c] * a + 255 * (1 - a));
    rgba[i + 3] = 255;
  }
}

/** A session with no role is a standalone embed, which has no guide to hide. */
export const useHidesGuide = () =>
  useSessionStore((s) => s.session?.role !== undefined && s.session.role !== "super_admin");

/** Enough for the largest preview on screen; a full 4K decode per image would stall the page. */
const MAX_EDGE = 1600;

function whiteGuideUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Without it the canvas is tainted by a cross-origin image and cannot be read back.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight, 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no 2d canvas"));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
      guideOverWhite(pixels.data, canvas.width, canvas.height);
      ctx.putImageData(pixels, 0, 0);
      canvas.toBlob((blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(new Error("no image"))), "image/png");
    };
    img.onerror = () => reject(new Error("The image did not load."));
    img.src = url;
  });
}

/**
 * The image a viewer may see at `url`: `url` itself for a super-admin, else its guide-free
 * copy, which is `undefined` until it is ready and when it cannot be made. The raw image is
 * never the stand-in.
 */
export function useGuideSafeUrl(url: string | null | undefined): string | undefined {
  const hide = useHidesGuide();
  const [safe, setSafe] = useState<{ of: string; url: string }>();
  useEffect(() => {
    if (!hide || !url) return;
    let live = true;
    let made: string | undefined;
    whiteGuideUrl(url).then(
      (white) => {
        made = white;
        if (live) setSafe({ of: url, url: white });
        else URL.revokeObjectURL(white);
      },
      () => {},
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [hide, url]);
  if (!url) return undefined;
  if (!hide) return url;
  return safe?.of === url ? safe.url : undefined;
}
